'use client';

import { c as _c } from "react/compiler-runtime";
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import type { JSX, MouseEvent, ReactNode } from 'react';
import { useEffect, useLayoutEffect, useState } from 'react';
import { AuthHeaderSection } from '@/components/header/auth-header-section';
import { CategoryNav } from '@/components/header/category-nav';
import { SearchInputButton } from '@/components/header/search-input-button';
import { resolveHeaderScrollMaterialMixPercent } from '@/components/layout/app-header-scroll-material';
import { AppBanners } from '@/components/layout/demo-mode-banner';
import { isSimpleHeaderRoute } from '@/components/layout/simple-header-routes';
import { LiveNavigationLabel } from '@/components/live/live-navigation-label';
import { MorfiLogo, MorfiVortexLogoCopyMenu } from '@/components/logo';
import { MorfiProductButton } from '@/components/support-mainnet/landing-product-link';
import { LandingContentReveal, LandingSectionReveal } from '@/components/support-mainnet/landing-section-reveal';
import { MorfiWaitlistButton } from '@/components/support-mainnet/morfi-waitlist-button';
import { AnimatedButton } from '@/components/ui/animated-button';
import { More01IconSolid } from '@/components/ui/icon/glyphs';
import { NavigationList, NavigationListItem } from '@/components/ui/navigation-list';
import { ProgressiveBlur } from '@/components/ui/progressive-blur';
import { desktopNavigationItems, isCategoryFilterListingSurface, isNavItemActive, navHrefWithCategory } from '@/config/navigation';
import { useLandingHeaderWaitlistReveal } from '@/hooks/ui/use-landing-header-waitlist-reveal';
import { useBetaAccess } from '@/hooks/use-beta-access';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { Link, usePathname } from '@/i18n/navigation'; // ARBITRARY: hash-only same-page section jumps on landing; network prefix does not apply
import { NetworkLink } from '@/i18n/network-link';
import { env } from '@/lib/config/env';
import { GUTTER_CLASS } from '@/lib/ui/layout';
import { elementIdFromHashHref, scrollToElementId } from '@/lib/ui/scroll-to-id';
import { cn } from '@/lib/ui/utils';
import { useLayoutStore } from '@/stores/layout';

/** Safe in-app home while public devnet is unavailable. Pre-launch, `/` renders
 *  the marketing landing, so a gated user must stay on `/markets`. MOR-734. */
const APP_HOME_HREF: string = env.NEXT_PUBLIC_EARLY_ACCESS_ENABLED === 'true' ? '/markets' : '/';

/** Synced to `document.documentElement` for {@link AppShell} main-row top padding. */
const APP_HEADER_CHROME_HEIGHT_VAR = '--app-header-chrome-height' as const;

/** Liquid Glass–style frost (inline — matches liquid-glass-nav frost pipeline). */
const HEADER_GLASS_BACKDROP_FILTER = 'blur(20px) saturate(180%) brightness(1.05)';
function headerScrollMaterialBackground(scrollY: number): string {
  return `color-mix(in oklch, var(--background-secondary) ${resolveHeaderScrollMaterialMixPercent(scrollY)}%, transparent)`;
}

/** Full frost through the nav row; softer bottom feather (scroll-edge falloff). */
const HEADER_BACKDROP_MASK = '[mask-image:linear-gradient(to_bottom,black_0%,black_78%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_bottom,black_0%,black_78%,transparent_100%)]';
function HeaderScrollBackdrop(t0) {
  const $ = _c(5);
  const {
    scrollY
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("pointer-events-none absolute inset-0 z-0", "bg-background-secondary/88 supports-backdrop-filter:bg-transparent", HEADER_BACKDROP_MASK);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== scrollY) {
    t2 = headerScrollMaterialBackground(scrollY);
    $[1] = scrollY;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <div aria-hidden="true" className={t1} style={{
      backdropFilter: HEADER_GLASS_BACKDROP_FILTER,
      WebkitBackdropFilter: HEADER_GLASS_BACKDROP_FILTER,
      backgroundColor: t2
    }} />;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

/** Landing nav links — 14px medium (design spec). */
const LANDING_NAV_LINK_CLASS = 'text-body font-medium text-foreground-secondary transition-colors hover:text-foreground-primary';
function onLandingSectionNavClick(event: MouseEvent<HTMLAnchorElement>): void {
  const href = event.currentTarget.getAttribute('href');
  if (href === null) {
    return;
  }
  const id = elementIdFromHashHref(href);
  if (id === null) {
    return;
  }
  event.preventDefault();
  scrollToElementId(id);
  window.history.pushState(null, '', `#${id}`); // ALLOW-NETWORK-UNAWARE: same-page hash sync; does not navigate.
}
function LandingSectionNavLink(t0) {
  const $ = _c(3);
  const {
    href,
    children
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== href) {
    t1 = <Link href={href} className={LANDING_NAV_LINK_CLASS} onClick={onLandingSectionNavClick}>{children}</Link>;
    $[0] = children;
    $[1] = href;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function LandingMarketingHeader(t0) {
  const $ = _c(40);
  const {
    headerRoot,
    setHeaderRoot
  } = t0;
  const tLanding = useTranslations("supportMainnet.landingHeader");
  const tHero = useTranslations("supportMainnet.hero");
  const productCtaRevealed = useLandingHeaderWaitlistReveal(headerRoot);
  const devnetEnabled = useFeatureGate("devnet_enabled");
  const t1 = devnetEnabled !== false ? "launchCtaWaitlist" : "ctaWaitlist";
  let t2;
  if ($[0] !== t1 || $[1] !== tHero) {
    t2 = tHero(t1);
    $[0] = t1;
    $[1] = tHero;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const productLabel = t2;
  const hasBetaAccess = useBetaAccess();
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ProgressiveBlur edge="top" tint={true} className="absolute inset-x-0 top-0 z-0" />;
    t4 = cn("relative z-10 pb-4", GUTTER_CLASS);
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <LandingSectionReveal className="shrink-0 justify-self-start" delay={0.05} trigger="mount"><MorfiVortexLogoCopyMenu size={32} /></LandingSectionReveal>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== tLanding) {
    t6 = tLanding("navigation");
    $[6] = tLanding;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== tLanding) {
    t7 = tLanding("about");
    $[8] = tLanding;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t7) {
    t8 = <LandingSectionReveal delay={0.1} trigger="mount"><LandingSectionNavLink href="#journey-mission-heading">{t7}</LandingSectionNavLink></LandingSectionReveal>;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== tLanding) {
    t9 = tLanding("howItWorks");
    $[12] = tLanding;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t9) {
    t10 = <LandingSectionReveal delay={0.15} trigger="mount"><LandingSectionNavLink href="#how-it-works-heading">{t9}</LandingSectionNavLink></LandingSectionReveal>;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== tLanding) {
    t11 = tLanding("team");
    $[16] = tLanding;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11) {
    t12 = <LandingSectionReveal delay={0.2} trigger="mount"><LandingSectionNavLink href="#team">{t11}</LandingSectionNavLink></LandingSectionReveal>;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t10 || $[21] !== t12 || $[22] !== t6 || $[23] !== t8) {
    t13 = <nav aria-label={t6} className="hidden items-center justify-center gap-8 md:flex md:justify-self-center">{t8}{t10}{t12}</nav>;
    $[20] = t10;
    $[21] = t12;
    $[22] = t6;
    $[23] = t8;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const t14 = devnetEnabled !== null;
  let t15;
  if ($[25] !== devnetEnabled || $[26] !== hasBetaAccess || $[27] !== productCtaRevealed || $[28] !== productLabel || $[29] !== tLanding) {
    t15 = devnetEnabled !== false ? productCtaRevealed ? <LandingContentReveal revealed={productCtaRevealed} hideWhenHidden={true}><MorfiProductButton className="morfi-waitlist-button--header" surface="header">{productLabel}</MorfiProductButton></LandingContentReveal> : null : hasBetaAccess ? <MorfiWaitlistButton href={APP_HOME_HREF} className="morfi-waitlist-button--header">{tLanding("enterApp")}</MorfiWaitlistButton> : <div className="flex items-center justify-end gap-4"><Link href="/early-access?card=code" className="shrink-0 text-body font-medium text-foreground-secondary transition-colors ease-default hover:text-foreground-primary">{tLanding("enterCode")}</Link>{productCtaRevealed ? <LandingContentReveal revealed={productCtaRevealed} hideWhenHidden={true}><MorfiWaitlistButton href="/early-access" className="morfi-waitlist-button--header">{productLabel}</MorfiWaitlistButton></LandingContentReveal> : null}</div>;
    $[25] = devnetEnabled;
    $[26] = hasBetaAccess;
    $[27] = productCtaRevealed;
    $[28] = productLabel;
    $[29] = tLanding;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t14 || $[32] !== t15) {
    t16 = <LandingSectionReveal className="shrink-0 justify-self-end" delay={0.25} trigger="mount"><LandingContentReveal revealed={t14} hideWhenHidden={true}>{t15}</LandingContentReveal></LandingSectionReveal>;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t13 || $[35] !== t16) {
    t17 = <div className={t4}><div className="grid h-16 grid-cols-[1fr_auto] items-center gap-4 md:grid-cols-[1fr_auto_1fr]">{t5}{t13}{t16}</div></div>;
    $[34] = t13;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== setHeaderRoot || $[38] !== t17) {
    t18 = <header ref={setHeaderRoot} className="fixed inset-x-0 top-0 z-sticky overflow-visible bg-transparent">{t3}{t17}</header>;
    $[37] = setHeaderRoot;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  return t18;
}
function AppLogoLink() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <NetworkLink href="/markets" className="flex shrink-0 items-center"><MorfiLogo size={32} mark={true} className="sm:hidden" /><MorfiLogo size={32} className="hidden sm:block" /></NetworkLink>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function AppHeader() {
  const $ = _c(50);
  const pathname = usePathname();
  const simpleHeader = isSimpleHeaderRoute(pathname);
  const activeCategory = useSearchParams().get("category");
  const setMobileMenuOpen = useLayoutStore(_temp);
  const t = useTranslations("header");
  const tNav = useTranslations("nav");
  const liveEventsEnabled = useFeatureGate("live_events") === true;
  const desktopItems = desktopNavigationItems(liveEventsEnabled);
  const [scrollY, setScrollY] = useState(0);
  const [headerRoot, setHeaderRoot] = useState(null);
  let t0;
  let t1;
  if ($[0] !== headerRoot) {
    t0 = () => {
      const el = headerRoot;
      if (el === null) {
        return;
      }
      const root = document.documentElement;
      const syncHeight = () => {
        root.style.setProperty(APP_HEADER_CHROME_HEIGHT_VAR, `${el.getBoundingClientRect().height}px`);
      };
      const ro = new ResizeObserver(syncHeight);
      ro.observe(el);
      syncHeight();
      return () => {
        ro.disconnect();
        root.style.removeProperty(APP_HEADER_CHROME_HEIGHT_VAR);
      };
    };
    t1 = [headerRoot];
    $[0] = headerRoot;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useLayoutEffect(t0, t1);
  let t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const updateScrolled = function updateScrolled() {
        setScrollY(window.scrollY);
      };
      updateScrolled();
      window.addEventListener("scroll", updateScrolled, {
        passive: true
      });
      return () => {
        window.removeEventListener("scroll", updateScrolled);
      };
    };
    t3 = [];
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  if (simpleHeader) {
    let t4;
    if ($[5] !== headerRoot || $[6] !== setHeaderRoot) {
      t4 = <LandingMarketingHeader headerRoot={headerRoot} setHeaderRoot={setHeaderRoot} />;
      $[5] = headerRoot;
      $[6] = setHeaderRoot;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  const t4 = "fixed inset-x-0 top-0 z-sticky overflow-hidden bg-transparent";
  let t5;
  if ($[8] !== scrollY) {
    t5 = <HeaderScrollBackdrop scrollY={scrollY} />;
    $[8] = scrollY;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <AppBanners />;
    t7 = cn("relative z-10 flex h-16 min-w-0 items-center", GUTTER_CLASS);
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  const t8 = "flex shrink-0 items-center gap-4";
  let t10;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <AppLogoLink />;
    t10 = <div className="h-5 w-px bg-border-primary hidden lg:block" />;
    $[12] = t10;
    $[13] = t9;
  } else {
    t10 = $[12];
    t9 = $[13];
  }
  const t11 = "hidden shrink-0 lg:block";
  const T0 = NavigationList;
  let t12;
  if ($[14] !== t) {
    t12 = t("navigation");
    $[14] = t;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const t13 = "none";
  const t14 = desktopItems.map(item => {
    const isActive = isNavItemActive(item, pathname);
    const label = item.key.replace("nav.", "") as keyof IntlMessages['nav'];
    return <NavigationListItem key={item.key} href={navHrefWithCategory(item.href, activeCategory)} active={isActive}>{item.key === "nav.live" ? <LiveNavigationLabel>{tNav(label)}</LiveNavigationLabel> : tNav(label)}</NavigationListItem>;
  });
  let t15;
  if ($[16] !== T0 || $[17] !== t12 || $[18] !== t14) {
    t15 = <div className={t11}><T0 aria-label={t12} edgeEffect={t13}>{t14}</T0></div>;
    $[16] = T0;
    $[17] = t12;
    $[18] = t14;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== t10 || $[21] !== t15 || $[22] !== t9) {
    t16 = <div className={t8}>{t9}{t10}{t15}</div>;
    $[20] = t10;
    $[21] = t15;
    $[22] = t9;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  let t18;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <SearchInputButton />;
    t18 = <AuthHeaderSection />;
    $[24] = t17;
    $[25] = t18;
  } else {
    t17 = $[24];
    t18 = $[25];
  }
  let t19;
  if ($[26] !== setMobileMenuOpen) {
    t19 = () => setMobileMenuOpen(true);
    $[26] = setMobileMenuOpen;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  let t20;
  if ($[28] !== t) {
    t20 = t("menu");
    $[28] = t;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  let t21;
  if ($[30] !== t19 || $[31] !== t20) {
    t21 = <div className="flex min-w-0 flex-1 items-center justify-end gap-1 sm:gap-2">{t17}{t18}<AnimatedButton variant="tertiary" size="sm" prefix={More01IconSolid} onClick={t19} aria-label={t20} /></div>;
    $[30] = t19;
    $[31] = t20;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== t16 || $[34] !== t21 || $[35] !== t7) {
    t22 = <div className={t7}>{t16}{t21}</div>;
    $[33] = t16;
    $[34] = t21;
    $[35] = t7;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== pathname) {
    t23 = !isCategoryFilterListingSurface(pathname) && "hidden lg:block";
    $[37] = pathname;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  let t24;
  if ($[39] !== t23) {
    t24 = cn("relative z-10 pb-4 pt-1", GUTTER_CLASS, "px-0 sm:px-0", t23);
    $[39] = t23;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  let t25;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <CategoryNav />;
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  let t26;
  if ($[42] !== t24) {
    t26 = <div className={t24}>{t25}</div>;
    $[42] = t24;
    $[43] = t26;
  } else {
    t26 = $[43];
  }
  let t27;
  if ($[44] !== setHeaderRoot || $[45] !== t22 || $[46] !== t26 || $[47] !== t5 || $[48] !== t6) {
    t27 = <header ref={setHeaderRoot} className={t4}>{t5}{t6}{t22}{t26}</header>;
    $[44] = setHeaderRoot;
    $[45] = t22;
    $[46] = t26;
    $[47] = t5;
    $[48] = t6;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  return t27;
}
function _temp(state) {
  return state.setMobileMenuOpen;
}